import { Tooltip, Typography } from '@mui/material';
import moment from 'moment-timezone';
import React, { useMemo } from 'react';
import { PersonVitalInsights, PersonVitalTimeFrame, DetailedSleepStage } from '@redux/person/personTypes';
import { CircledCustomIcon, TableIcon } from 'components/Icons';
import { Text } from 'components/Typography';
import { ChartProps } from 'containers/PatientDashboard/components/Charts/Charts';
import { createSleepTooltip, getMinTime } from 'utils/charts';
import { Color } from 'utils/color';
import { getDateFromStringIgnoreTimezone } from 'utils/time';
import style from './SleepTimeline.scss';

type SleepTimelineProps = Omit<ChartProps, 'vitalType' | 'isLoadingData'>;

const formatSleepStage = (stage: DetailedSleepStage) => {
  switch (stage) {
    case DetailedSleepStage.AWAKE:
      return 'Awake';
    case DetailedSleepStage.LIGHT_SLEEP:
      return 'Light';
    case DetailedSleepStage.DEEP_SLEEP:
      return 'Deep';
    case DetailedSleepStage.REM_SLEEP:
      return 'REM';
    default:
      return '';
  }
};

export const SleepTimeline: React.FC<SleepTimelineProps> = ({
  timeZoneId,
  personVitalInsights,
  timeFrame,
  selectedDate,
  minControlRate,
  maxControlRate,
  onChartDataAvailabilityChange
}) => {
  const isToday = moment.tz(selectedDate, timeZoneId).isSame(moment().tz(timeZoneId), 'day');
  const xMaxMoment = isToday ? moment().tz(timeZoneId) : moment.tz(selectedDate, timeZoneId).startOf('day').add(1, 'day').hour(17).minute(0).second(0);
  const xMaxValue = getDateFromStringIgnoreTimezone(xMaxMoment.format('YYYY-MM-DD HH:mm:ss z'))!;
  const xMinValue = getMinTime(xMaxValue, timeFrame);

  const isDateValid = (dt: Date | null) => Boolean(dt && dt >= xMinValue && dt <= xMaxValue);

  // Prepare segments for manual timeline rendering
  const segments = useMemo(() => {
    if (!personVitalInsights?.listOfAggregatedSleepData) return [];

    // Convert the raw data into segments and filter by valid dates
    const rawSegs = personVitalInsights.listOfAggregatedSleepData
      .map((vs) => {
        const start = getDateFromStringIgnoreTimezone(vs.startTime);
        const end = getDateFromStringIgnoreTimezone(vs.endTime);
        if (start && end && isDateValid(start) && isDateValid(end)) {
          return {
            start,
            end,
            stage: formatSleepStage(vs.detailedSleepStage)
          };
        }
        return null;
      })
      .filter((s): s is { start: Date; end: Date; stage: string } => Boolean(s))
      .sort((a, b) => a.start.getTime() - b.start.getTime());

    // Merge contiguous/overlapping segments of the same stage
    const merged: { start: Date; end: Date; stage: string }[] = [];
    rawSegs.forEach((seg) => {
      const last = merged[merged.length - 1];
      if (last && last.stage === seg.stage && seg.start.getTime() <= last.end.getTime()) {
        // Extend the last segment when stages match and time overlaps or is contiguous
        if (seg.end.getTime() > last.end.getTime()) {
          last.end = seg.end;
        }
      } else {
        merged.push({ ...seg });
      }
    });

    return merged.map((m) => ({
      ...m,
      tooltip: createSleepTooltip(m.start, m.end, m.stage, timeFrame)
    }));
  }, [personVitalInsights, xMinValue, xMaxValue, timeFrame]);

  // Notify parent about data availability
  React.useEffect(() => {
    onChartDataAvailabilityChange?.(segments.length > 0);
  }, [segments.length, onChartDataAvailabilityChange]);

  // Generate X-axis labels every 2 hours, rounded to nearest hour
  const xLabels = useMemo(() => {
    // Round start to nearest hour
    const labels: Date[] = [];
    const first = new Date(xMinValue);
    const m = first.getMinutes();
    if (m >= 30) {
      first.setHours(first.getHours() + 1);
    }
    first.setMinutes(0, 0, 0);
    const cur = new Date(first);
    while (cur <= xMaxValue) {
      labels.push(new Date(cur));
      cur.setHours(cur.getHours() + 2);
    }
    return labels;
  }, [xMinValue, xMaxValue]);

  // Parse awakening timestamps within current range
  const awakeningTimes = useMemo(() => {
    if (!personVitalInsights?.bedExits) return [];
    return personVitalInsights.bedExits.map((t) => getDateFromStringIgnoreTimezone(t)).filter((d): d is Date => isDateValid(d));
  }, [personVitalInsights?.bedExits, xMinValue, xMaxValue]);
  const maxLevel = 4;

  return timeFrame === PersonVitalTimeFrame.TWENTY_FOUR_HOURS && segments.length > 0 ? (
    // If there are no bed exits, don't show the legend
    <div>
      <div className={style.chartWrapper}>
        <div className={style.yAxis}>
          {['Awake', 'Light', 'Deep', 'REM'].map((label, i) => {
            const idx = i + 1;
            const pos = ((maxLevel - idx) / maxLevel) * 100;
            return (
              <Typography key={label} component="div" variant="caption" className={style.yLabel} style={{ top: `${pos}%` }}>
                {label}
              </Typography>
            );
          })}
        </div>
        {!isToday && (
          <div className={style.dateAxis}>
            <div className={style.dateLabelLeft}>{moment.tz(selectedDate, timeZoneId).format('MMM D')}</div>
            <div className={style.dateLabelRight}>{moment.tz(selectedDate, timeZoneId).add(1, 'day').format('MMM D')}</div>
          </div>
        )}
        <div className={style.plotArea}>
          {/* Horizontal grid lines */}
          {Array.from({ length: maxLevel + 1 }).map((_, i) => (
            <div key={`h${i}`} className={style.hGrid} style={{ top: `${(i / maxLevel) * 100}%` }} />
          ))}
          {/* Vertical grid lines */}
          {xLabels.slice(1, -1).map((t, i) => {
            const totalMs = xMaxValue.getTime() - xMinValue.getTime();
            const left = ((t.getTime() - xMinValue.getTime()) / totalMs) * 100;
            return <div key={`v${i}`} className={style.vGrid} style={{ left: `${left}%` }} />;
          })}
          {/* Segments */}
          {segments.map((seg, i) => {
            const totalMs = xMaxValue.getTime() - xMinValue.getTime();
            const left = ((seg.start.getTime() - xMinValue.getTime()) / totalMs) * 100;
            const width = ((seg.end.getTime() - seg.start.getTime()) / totalMs) * 100;
            const stageMap: Record<string, number> = { Awake: 1, Light: 2, Deep: 3, REM: 4 };
            const idx = stageMap[seg.stage] || 0;
            const heightPct = 100 / maxLevel;
            const topPct = ((maxLevel - idx) / maxLevel) * 100;
            const colorMap: Record<string, string> = {
              Awake: Color.DARK_BLUE_100,
              Light: Color.DARK_BLUE_300,
              Deep: Color.DARK_BLUE_500,
              REM: Color.DARK_BLUE_700
            };
            return (
              <Tooltip
                key={i}
                title={<div dangerouslySetInnerHTML={{ __html: seg.tooltip }} />}
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      padding: 0
                    }
                  },
                  arrow: {
                    sx: {
                      color: 'white'
                    }
                  }
                }}
              >
                <div
                  className={style.segment}
                  style={{
                    left: `${left}%`,
                    width: `${width}%`,
                    top: `${topPct}%`,
                    height: `${heightPct}%`,
                    backgroundColor: colorMap[seg.stage] || Color.GRAY_400
                  }}
                />
              </Tooltip>
            );
          })}
          {/* Awakening markers */}
          {awakeningTimes.map((t, i) => {
            const totalMs = xMaxValue.getTime() - xMinValue.getTime();
            const left = ((t.getTime() - xMinValue.getTime()) / totalMs) * 100;
            return <div key={`a${i}`} className={style.awakeningLine} style={{ left: `${left}%` }} />;
          })}
        </div>
        {/* X-axis labels */}
        <div className={style.xAxis}>
          {xLabels.map((t, idx) => {
            const totalMs = xMaxValue.getTime() - xMinValue.getTime();
            const left = ((t.getTime() - xMinValue.getTime()) / totalMs) * 100;
            const labelStyle: React.CSSProperties = { left: `${left}%` };
            if (idx === 0) {
              labelStyle.transform = 'none';
            } else if (idx === xLabels.length - 1) {
              labelStyle.transform = 'translateX(-100%)';
            }
            return (
              <div key={t.getTime()} className={style.xLabel} style={labelStyle}>
                {moment(t).format('HH:00')}
              </div>
            );
          })}
        </div>
      </div>
      {awakeningTimes.length > 0 && (
        <div className={style.legend}>
          <div className={style.legendItem}>
            <div className={style.legendColorBox} style={{ backgroundColor: Color.PURPLE_600 }} />
            <Typography variant="caption">Bed Exits</Typography>
          </div>
        </div>
      )}
      <div className={style.chartWrapper} />
    </div>
  ) : (
    <div className={style.noDataChartContainer}>
      <CircledCustomIcon icon={TableIcon} />
      <Text>No data to display</Text>
    </div>
  );
};

export default SleepTimeline;
